/* Rhody Reviews api/v1 -- account domain. PURE EXTRACTION from lib/api.ts (gate-prep split); no behavior change. */
import { request } from './_http';
import type { GoogleConnection, GoogleCandidate } from './google';

/* Business profile + brand voice (GET/PUT /business). */
export interface BusinessProfile {
  business_name: string;
  industry: string;
  contact_phone: string;
  contact_email: string;
  /* Added 2026-06-29 (backend shipped these). business_address is the field
     that was missing -- the NAP "physical address" of the business. The rest
     round out the profile so a Google pull can auto-fill the whole thing.
     business_hours is a weekday LIST ([] when unset). google_location_id is the
     Google place_id -- NEVER render it to the owner (carried behind the scenes). */
  business_address?: string | null;
  business_website?: string | null;
  primary_category?: string | null;
  business_hours?: string[];
  google_location_id?: string | null;
  /* The client's ONE review-request photo (of them / their team / their work
     vehicle), injected at the top of every review-request email. Set via
     POST /business/photo, cleared via DELETE. Optional: the business-profile
     GET may not surface it on every build -- the UI treats absent as "no photo
     shown on load" and relies on the POST/DELETE responses within a session. */
  request_photo_url?: string | null;
  request_photo_uploaded_at?: string | null;
}
export interface BrandVoice {
  tone?: string;
  formality?: string;
  signature?: string;
  do_not_say?: string;
  sample_phrases?: string;
  auto_draft_enabled?: boolean;
  business_address?: string;
  /* Owner-configurable monthly review goal (drives the Dashboard goal ring).
     Persisted in the brand_voice blob via PUT /business. */
  monthly_review_goal?: number;
  /* Connect-first booking (2026-06-26). All persisted in the brand_voice blob
     via PUT /business (no schema change). booking_url is surfaced as a "Book"
     button on the public review widget; request_time_enabled turns on the
     minimal native request-a-time fallback for owners with no booking tool. */
  booking_url?: string;
  booking_label?: string;
  booking_show_on_widget?: boolean;
  request_time_enabled?: boolean;
  business_hours?: string;
  [k: string]: unknown;
}
export interface BusinessResponse {
  client_id: string;
  profile: BusinessProfile;
  brand_voice: BrandVoice;
  /* Canonical Google connection block (2026-07-07). THE source of truth for
     connected-state everywhere; see lib/api/google.ts. Optional so older API
     responses don't break typing. */
  google_connection?: GoogleConnection;
  /* The auto-selected authenticated listing awaiting an EXPLICIT confirm.
     NON-NULL only when google_connection.google_connection_status ===
     'listing_pending_confirmation'. */
  google_candidate?: GoogleCandidate | null;
}
export interface BusinessUpdate {
  business_name?: string;
  industry?: string;
  contact_phone?: string;
  contact_email?: string;
  /* Partial PATCH supported. An empty string CLEARS business_address /
     business_website / primary_category / google_location_id. business_hours
     MUST be sent as a LIST (a non-list 400s). */
  business_address?: string;
  business_website?: string;
  primary_category?: string;
  business_hours?: string[];
  google_location_id?: string;
  brand_voice?: BrandVoice;
}

/* POST /business/populate-from-place {place_id} -> auto-fills the profile from
   the Google place. populated_fields lists what Google actually filled (so we
   can tell the owner exactly what got pulled). Never expose the place_id. */
export interface PopulateFromPlaceResult {
  ok: boolean;
  place_id: string;
  populated_fields: string[];
  profile: BusinessProfile;
}

/* Private-feedback config (GET/PUT /feedback-gate). An always-available
   parallel channel - NOT a score-based gate. No threshold field exists by
   design (Hard Rule #4 / FTC 16 CFR Part 465). */
export type FeedbackRoute = 'private_form' | 'text_owner' | 'email_support';
export interface FeedbackGateConfig {
  enabled: boolean;
  route_to: FeedbackRoute;
  private_intro: string;
}
export interface FeedbackGateResponse {
  client_id: string;
  defaulted?: boolean;
  config: FeedbackGateConfig;
  note?: string;
}
export type FeedbackGateUpdate = Partial<FeedbackGateConfig>;

/* Team invitations (GET/POST /team). */
export type TeamRole = 'admin' | 'approver' | 'viewer';
export interface TeamMember {
  id: string;
  email: string;
  role: TeamRole | string;
  invited_at: string | null;
  accepted_at: string | null;
}
export interface TeamResponse {
  client_id: string;
  count: number;
  team: TeamMember[];
}
export interface TeamInviteResult {
  client_id: string;
  member: TeamMember;
  already_invited?: boolean;
}

/* Account deletion (DELETE /api/v1/account). Irreversible. `confirm` must be
   the exact word DELETE or the exact business name (backend safety belt). */
export interface DeleteAccountResult {
  ok: boolean;
  client_id: string;
  subscription_cancelled: boolean;
  auth_user_deleted: boolean;
  purged: Record<string, number>;
  deleted_at: string;
}

/* Client review-request photo (POST/DELETE /business/photo). The photo rides at
   the top of every review-request email a face lifts response rate. */
export interface SetPhotoResult {
  photo_url: string;
  uploaded_at: string;
  width?: number;
  height?: number;
}
export interface DeletePhotoResult {
  ok: boolean;
  photo_url: null;
}

export const accountApi = {
  /* ----- Business profile + brand voice ----- */
  getBusiness(signal?: AbortSignal): Promise<BusinessResponse> {
    return request<BusinessResponse>('/api/v1/business', { signal });
  },
  /* Set (upload) this client's review-request photo. We send the image as a
     base64 data URL through the shared JSON request() path -- the endpoint also
     accepts multipart/raw, but JSON keeps us on the one code path. The endpoint
     validates + re-encodes + EXIF-strips server-side, so any JPG/PNG/HEIC-ish
     phone photo is fine. A 503 (photo_storage_unavailable) means migration #36
     is not applied yet -- the caller renders the calm "being enabled" state. */
  setBusinessPhoto(imageDataUrl: string): Promise<SetPhotoResult> {
    return request<SetPhotoResult>('/api/v1/business/photo', {
      method: 'POST',
      body: { image_base64: imageDataUrl },
    });
  },
  deleteBusinessPhoto(): Promise<DeletePhotoResult> {
    return request<DeletePhotoResult>('/api/v1/business/photo', { method: 'DELETE' });
  },
  updateBusiness(patch: BusinessUpdate): Promise<BusinessResponse> {
    return request<BusinessResponse>('/api/v1/business', {
      method: 'PUT',
      body: patch,
    });
  },
  /* Auto-fill the profile from a Google place. The place_id comes from the
     existing /places/search picker; we never show it to the owner. */
  populateBusinessFromPlace(placeId: string): Promise<PopulateFromPlaceResult> {
    return request<PopulateFromPlaceResult>('/api/v1/business/populate-from-place', {
      method: 'POST',
      body: { place_id: placeId },
    });
  },

  /* ----- Feedback gate config ----- */
  getFeedbackGate(signal?: AbortSignal): Promise<FeedbackGateResponse> {
    return request<FeedbackGateResponse>('/api/v1/feedback-gate', { signal });
  },
  updateFeedbackGate(patch: FeedbackGateUpdate): Promise<FeedbackGateResponse> {
    return request<FeedbackGateResponse>('/api/v1/feedback-gate', {
      method: 'PUT',
      body: patch,
    });
  },

  /* ----- Team invitations ----- */
  listTeam(signal?: AbortSignal): Promise<TeamResponse> {
    return request<TeamResponse>('/api/v1/team', { signal });
  },
  inviteTeamMember(input: { email: string; role?: TeamRole }): Promise<TeamInviteResult> {
    return request<TeamInviteResult>('/api/v1/team', { method: 'POST', body: input });
  },

  /* ----- Account deletion (irreversible) ----- */
  deleteAccount(confirm: string): Promise<DeleteAccountResult> {
    return request<DeleteAccountResult>('/api/v1/account', {
      method: 'DELETE',
      body: { confirm },
    });
  },
};
